import { AbstractDialog } from "../abstract_dialog.js";
import { DialogId } from "../dialog_id.js";
import { DialogManager } from "../dialog_manager.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Vector2 } from "../../../common/vector2.js";
import { LinkedSocialProvider, providerDisplayName } from "../account_providers_preview_state.js";
import { ApiError, NetworkError } from "../../../api/http_client.js";
import type { LinkErrorReason } from "../../../api/account_link_service.js";

export interface AuthMessageDialogOptions
{
  title_key: string;
  text_key:  string;
  params?:   string[];
  icon?:     ImgID;
  on_ok?:    () => void;
}

export class AuthMessageDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.AUTH_MESSAGE_DIALOG;

  private static readonly BTN_Y_DESIGN = 545;

  private close_btn!: UIImage;

  constructor( private readonly options: AuthMessageDialogOptions )
  {
    const design_body_w = 910;
    const design_body_h = 718;

    const body_w = LayoutScaler.widthFromDesignAdaptive( design_body_w );
    const body_h = LayoutScaler.heightFromDesign( design_body_h );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.createBackground();
    this.createTopBgPart();
    this.createTitle();
    this.createText();
    this.createOkButton();
    this.createCloseButton();
  }

  public static providerIcon( provider: LinkedSocialProvider ): ImgID
  {
    switch ( provider )
    {
      case LinkedSocialProvider.Google:   return ImgID.GoogleIcon;
      case LinkedSocialProvider.Facebook: return ImgID.FacebookIcon;
      case LinkedSocialProvider.Apple:    return ImgID.AppleIcon;
    }
  }

  public static forLinkError( provider: LinkedSocialProvider, reason: LinkErrorReason ): AuthMessageDialog
  {
    const text_key = reason === 'unavailable' ? "AUTH_ERROR.UNAVAILABLE"
      : reason === 'disabled' ? "AUTH_ERROR.DISABLED"
      : reason === 'network' ? "AUTH_ERROR.NETWORK"
      : "AUTH_ERROR.GENERIC";

    return new AuthMessageDialog( {
      title_key: "AUTH_ERROR.TITLE",
      text_key,
      params: [ providerDisplayName( provider ) ],
      icon: AuthMessageDialog.providerIcon( provider ),
    } );
  }

  public static forDisconnectError( provider: LinkedSocialProvider, error: unknown ): AuthMessageDialog
  {
    return new AuthMessageDialog( {
      title_key: "AUTH_ERROR.SWITCH_TITLE",
      text_key: error instanceof NetworkError ? "AUTH_ERROR.NETWORK" : "AUTH_ERROR.DISCONNECT_FAILED",
      params: [ providerDisplayName( provider ) ],
      icon: AuthMessageDialog.providerIcon( provider ),
    } );
  }

  public static forAccountError( error: unknown, fallback_key: string, on_ok?: () => void ): AuthMessageDialog
  {
    const offline = error instanceof NetworkError || ( error instanceof ApiError && error.status === 503 );
    return new AuthMessageDialog( {
      title_key: "AUTH_ERROR.SWITCH_TITLE",
      text_key: offline ? "AUTH_ERROR.NETWORK" : fallback_key,
      icon: ImgID.UI_WarningIcon,
      on_ok,
    } );
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( 0 ),
      LayoutScaler.yFromDesign( 0 ),
      this.rect.getW(),
      this.rect.getH(),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private createTopBgPart(): void
  {
    const design_w = 237;
    const design_h = 128;
    const design_y = -80;
    const design_icon_size = 134;
    const design_icon_y = 55;

    const part_w = LayoutScaler.widthFromDesign( design_w );
    const part_h = LayoutScaler.heightFromDesign( design_h );
    const part_x = this.rect.getW2() - part_w / 2;
    const part_y = LayoutScaler.yFromDesign( design_y );

    const bg_part = new UIImage(
      this.ctx,
      part_x,
      part_y,
      part_w,
      part_h,
      ImgID.UI_CapsuleTopBgPart,
      {},
      false
    );
    this.addChild( bg_part );

    const icon_w = LayoutScaler.widthFromDesign( design_icon_size );
    const icon_h = LayoutScaler.heightFromDesign( design_icon_size );
    const icon_x = Math.ceil( ( part_w - icon_w ) / 2 );
    const icon_y = LayoutScaler.yFromDesign( design_icon_y );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      this.options.icon ?? ImgID.UI_WarningIcon
    );
    bg_part.addChild( icon, true );
  }

  private createTitle(): void
  {
    const design_h = 132;
    const design_y = 140;
    const design_font_size = 60;
    const design_outline_w = 20;

    const label_w = this.rect.getW() * 0.8;
    const label_h = LayoutScaler.heightFromDesign( design_h );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_y );

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: this.options.title_key,
        text_variable_params: this.options.params ?? [],
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.booster_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createText(): void
  {
    const design_title_bottom_y = 272;
    const design_w = 760;
    const design_font_size = 46;
    const design_outline_w = 10;

    const label_w = LayoutScaler.widthFromDesign( design_w );
    const label_x = this.rect.getW2() - label_w / 2;
    const label_y = LayoutScaler.yFromDesign( design_title_bottom_y );
    const label_h = LayoutScaler.yFromDesign( AuthMessageDialog.BTN_Y_DESIGN ) - label_y;

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: this.options.text_key,
        text_variable_params: this.options.params ?? [],
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        auto_wrap: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( design_outline_w ),
        outline_color: ColorScheme.settings_dialog_title_outline,
      }
    );

    this.addChild( label );
  }

  private createOkButton(): void
  {
    const design_btn_w = 380;
    const design_btn_h = 112;
    const design_font_size = 47;

    const btn_w = LayoutScaler.widthFromDesign( design_btn_w );
    const btn_h = LayoutScaler.heightFromDesign( design_btn_h );
    const btn_x = this.rect.getW2() - btn_w / 2;
    const btn_y = LayoutScaler.yFromDesign( AuthMessageDialog.BTN_Y_DESIGN );

    const ok_btn = new RoundedRectangle(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      btn_h / 2,
      "#10B2E2",
      undefined,
      true
    );
    ok_btn.addChild( new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "OK",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: LayoutScaler.textSizeFromDesign( design_font_size ),
        font_weight: 700,
        using_y_offset: false,
        ...TextStyleScheme.buttonShadow(),
      }
    ), true );
    ok_btn.setOnClick( () => this.dismiss() );
    this.addChild( ok_btn );
  }

  private dismiss(): void
  {
    this.close();
    this.options.on_ok?.();
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const design_offset_x = 10;
    const design_offset_y = 100;

    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( design_offset_x );
    const btn_y = -LayoutScaler.yFromDesign( design_offset_y );

    this.close_btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    this.close_btn.setOnClick( () => this.dismiss() );
    this.addChild( this.close_btn );
  }

  protected override playAppearAnimation(
    from_scale      = 0.7,
    overshoot_scale = 1.1,
    final_scale     = 1.0,
    scale_up_duration = 136,
    settle_duration   = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );

    super.playAppearAnimation( from_scale, overshoot_scale, final_scale, scale_up_duration, settle_duration );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public name(): string { return "AuthMessageDialog"; }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
